// Black-and-white symbols for the shapes table: our own silhouettes, drawn in a 100 box. One outline each, and any
// further pieces; holes and pieces are joined to it by a hairline seam so a symbol is a single filled stroke that
// moves as one (the fill is nonzero, so overlapping pieces add up and holes take away).

import type { Pt } from '../stencil/geom';

export interface Symbol { key: string; label: string; outer: Pt[]; holes: Pt[][]; more: { outer: Pt[]; holes: Pt[][] }[] }

const area = (p: Pt[]) => p.reduce((a, q, i) => { const n = p[(i + 1) % p.length]; return a + q[0] * n[1] - n[0] * q[1]; }, 0) / 2;
const mirror = (half: Pt[]): Pt[] => [...half, ...half.slice(1, -1).reverse().map(([x, y]) => [100 - x, y] as Pt)];
const ngon = (n: number, r: number, cx = 50, cy = 50, a0 = -Math.PI / 2, f: (i: number) => number = () => 1): Pt[] =>
  Array.from({ length: n }, (_, i) => { const a = a0 + (i / n) * Math.PI * 2, k = r * f(i); return [cx + Math.cos(a) * k, cy + Math.sin(a) * k] as Pt; });
const oval = (cx: number, cy: number, rx: number, ry: number, rot = 0, n = 20): Pt[] =>
  Array.from({ length: n }, (_, i) => {
    const a = (i / n) * Math.PI * 2, x = Math.cos(a) * rx, y = Math.sin(a) * ry;
    return [cx + x * Math.cos(rot) - y * Math.sin(rot), cy + x * Math.sin(rot) + y * Math.cos(rot)] as Pt;
  });

const crescentRing = (): Pt[] => {
  const c1: Pt = [50, 50], r1 = 44, c2: Pt = [64, 44], r2 = 36, N = 120, out: Pt[] = [];
  const on = (c: Pt, r: number, i: number): Pt => [c[0] + Math.cos((i / N) * 6.2832) * r, c[1] + Math.sin((i / N) * 6.2832) * r];
  const outside = (p: Pt) => Math.hypot(p[0] - c2[0], p[1] - c2[1]) > r2;
  const inside = (p: Pt) => Math.hypot(p[0] - c1[0], p[1] - c1[1]) < r1;
  const arc = (c: Pt, r: number, keep: (p: Pt) => boolean): Pt[] => {
    const pts = Array.from({ length: N }, (_, i) => on(c, r, i)), k = pts.map(keep);
    const s = k.findIndex((v, i) => v && !k[(i + N - 1) % N]);
    return Array.from({ length: N }, (_, i) => pts[(s + i) % N]).filter((_, i) => k[(s + i) % N]);
  };
  out.push(...arc(c1, r1, outside), ...arc(c2, r2, inside).reverse());
  return out;
};

const heart = (): Pt[] => Array.from({ length: 40 }, (_, i) => {
  const t = (i / 40) * Math.PI * 2;
  return [50 + 2.75 * 16 * Math.sin(t) ** 3, 46 - 2.75 * (13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t))] as Pt;
});

const almond = (): Pt[] => {
  const up = Array.from({ length: 13 }, (_, i) => { const x = 6 + (i / 12) * 88, u = (x - 50) / 44; return [x, 50 - 34 * (1 - u * u)] as Pt; });
  const dn = up.slice(1, -1).reverse().map(([x, y]) => [x, 100 - y] as Pt);
  return [...up, ...dn];
};

const spider = (): Pt[] => {
  const legs = [-55, -25, 12, 48].flatMap(d => [d, 180 - d]).map(d => (d * Math.PI) / 180);
  return Array.from({ length: 240 }, (_, i) => {
    const a = (i / 240) * Math.PI * 2;
    let r = 17;
    for (const l of legs) { const d = Math.atan2(Math.sin(a - l), Math.cos(a - l)); r += 31 * Math.exp(-((d / 0.075) ** 2)); }
    return [50 + Math.cos(a) * r, 54 + Math.sin(a) * r * 0.9] as Pt;
  });
};

const arc = (cx: number, cy: number, rx: number, ry: number, a0: number, a1: number, n = 16): Pt[] =>
  Array.from({ length: n + 1 }, (_, i) => { const a = a0 + ((a1 - a0) * i) / n; return [cx + Math.cos(a) * rx, cy + Math.sin(a) * ry] as Pt; });
const box = (x0: number, y0: number, x1: number, y1: number): Pt[] => [[x0, y0], [x1, y0], [x1, y1], [x0, y1]];
const PI = Math.PI;

const teardrop = (): Pt[] => Array.from({ length: 40 }, (_, i) => {
  const t = (i / 40) * PI * 2;
  return [50 + 46 * Math.sin(t) * Math.sin(t / 2), 50 - 44 * Math.cos(t)] as Pt;
});

const spade = (): Pt[] => heart().map(([x, y]) => [50 + (x - 50) * 0.86, 100 - y * 0.86 - 10] as Pt);

const candyCorn = (): Pt[] => [[50, 4], [62, 30], [76, 66], [80, 84], [70, 94], [30, 94], [20, 84], [24, 66], [38, 30]];

const web = (): [Pt[], Pt[][]][] => {
  const ring = (r: number, w: number): [Pt[], Pt[][]] => [ngon(8, r, 50, 50, -PI / 8), [ngon(8, r - w, 50, 50, -PI / 8)]];
  const spokes = Array.from({ length: 4 }, (_, i) => {
    const a = (i / 4) * PI, c = Math.cos(a), sn = Math.sin(a), w = 1.6;
    return [[50 + c * 48 - sn * w, 50 + sn * 48 + c * w], [50 + c * 48 + sn * w, 50 + sn * 48 - c * w],
      [50 - c * 48 + sn * w, 50 - sn * 48 - c * w], [50 - c * 48 - sn * w, 50 - sn * 48 + c * w]] as Pt[];
  });
  return [ring(30, 3.4), ring(15, 3), ...spokes.map(p => [p, []] as [Pt[], Pt[][]])];
};

type Entry = [string, string, Pt[], Pt[][]?, [Pt[], Pt[][]][]?];
const list: Entry[] = [
  // shapes
  ['triangle', 'Triangle', [[50, 8], [94, 88], [6, 88]]],
  ['circle', 'Circle', ngon(48, 44)],
  ['square', 'Square', ngon(4, 58, 50, 50, -PI / 4)],
  ['oval', 'Oval', oval(50, 50, 30, 45, 0, 36)],
  ['diamond', 'Diamond', [[50, 4], [88, 50], [50, 96], [12, 50]]],
  ['pentagon', 'Pentagon', ngon(5, 47, 50, 54)],
  ['hexagon', 'Hexagon', ngon(6, 46, 50, 50, 0)],
  ['star', 'Star', ngon(10, 46, 50, 54, -PI / 2, i => (i % 2 ? 0.42 : 1))],
  ['six-star', 'Six star', ngon(12, 46, 50, 50, -PI / 2, i => (i % 2 ? 0.56 : 1))],
  ['sparkle', 'Sparkle', ngon(8, 47, 50, 50, -PI / 2, i => (i % 2 ? 0.2 : 1))],
  ['heart', 'Heart', heart()],
  ['crescent', 'Crescent', crescentRing()],
  ['ring', 'Ring', ngon(48, 44), [ngon(40, 26)]],
  ['dome', 'Dome', arc(50, 76, 44, 64, PI, 2 * PI, 24)],
  ['teardrop', 'Drop', teardrop()],
  ['arrow', 'Arrow', [[6, 38], [56, 38], [56, 14], [95, 50], [56, 86], [56, 62], [6, 62]]],
  ['plus', 'Plus', [[38, 8], [62, 8], [62, 38], [92, 38], [92, 62], [62, 62], [62, 92], [38, 92], [38, 62], [8, 62], [8, 38], [38, 38]]],
  ['lightning', 'Lightning', [[60, 4], [26, 54], [46, 54], [36, 96], [76, 40], [54, 40], [72, 4]]],
  ['hourglass', 'Hourglass', [[14, 6], [86, 6], [86, 13], [56, 50], [86, 87], [86, 94], [14, 94], [14, 87], [44, 50], [14, 13]]],
  ['spade', 'Spade', spade(), [], [[[[50, 56], [64, 96], [36, 96]], []]]],
  ['club', 'Club', oval(50, 28, 19, 19), [], [[oval(29, 56, 19, 19), []], [oval(71, 56, 19, 19), []], [[[50, 46], [62, 96], [38, 96]], []]]],
  // Halloween
  ['pumpkin', 'Pumpkin', oval(50, 60, 20, 34), [], [[oval(32, 60, 20, 31), []], [oval(68, 60, 20, 31), []], [[[45, 30], [54, 30], [60, 8], [52, 6]], []]]],
  ['jack', 'Jack', oval(50, 58, 46, 38, 0, 40), [[[28, 40], [40, 40], [34, 28]], [[60, 40], [72, 40], [66, 28]], [[46, 54], [54, 54], [50, 46]],
    [[24, 64], [34, 70], [40, 64], [50, 72], [60, 64], [66, 70], [76, 64], [70, 80], [50, 86], [30, 80]]],
    [[[[45, 22], [55, 22], [60, 4], [51, 3]], []]]],
  ['bat', 'Bat', mirror([[50, 28], [55, 14], [59, 28], [70, 24], [82, 14], [95, 26], [96, 54], [86, 47], [78, 54], [72, 68], [64, 58], [58, 68], [50, 84]])],
  ['cat', 'Cat', [[22, 6], [38, 28], [62, 28], [78, 6], [90, 46], [84, 76], [66, 92], [34, 92], [16, 76], [10, 46]],
    [[[24, 54], [42, 50], [40, 64]], [[76, 54], [58, 50], [60, 64]], [[46, 70], [54, 70], [50, 77]]]],
  ['ghost', 'Ghost', [[16, 92], [16, 42], [22, 22], [36, 10], [50, 7], [64, 10], [78, 22], [84, 42], [84, 92], [74, 80], [64, 92], [50, 80], [36, 92], [26, 80]],
    [oval(38, 40, 6, 9), oval(62, 40, 6, 9), oval(50, 64, 5, 8)]],
  ['skull', 'Skull', mirror([[50, 6], [70, 10], [84, 24], [88, 44], [80, 60], [74, 66], [74, 88], [62, 93], [58, 83], [50, 93]]),
    [oval(34, 46, 9, 10), oval(66, 46, 9, 10), [[50, 58], [44, 72], [56, 72]]]],
  ['bone', 'Bone', box(20, 43, 80, 57), [], [[oval(20, 40, 11, 11), []], [oval(20, 60, 11, 11), []], [oval(80, 40, 11, 11), []], [oval(80, 60, 11, 11), []]]],
  ['spider', 'Spider', spider()],
  ['web', 'Web', ngon(8, 48, 50, 50, -PI / 8), [ngon(8, 44.5, 50, 50, -PI / 8)], web()],
  ['owl', 'Owl', mirror([[50, 16], [70, 4], [76, 20], [84, 40], [84, 70], [74, 88], [50, 96]]),
    [oval(36, 40, 10, 10), oval(64, 40, 10, 10), [[50, 50], [45, 60], [55, 60]]]],
  ['crow', 'Crow', [[2, 46], [22, 40], [30, 28], [40, 22], [50, 24], [56, 34], [72, 40], [97, 30], [88, 48], [70, 62], [56, 66], [54, 80], [60, 92],
    [50, 92], [46, 80], [40, 80], [38, 92], [30, 92], [34, 74], [26, 62], [22, 52]], [oval(38, 32, 3, 3, 0, 10)]],
  ['witch-hat', 'Witch hat', [[66, 4], [52, 14], [42, 34], [34, 62], [8, 68], [6, 80], [94, 80], [92, 68], [66, 62], [58, 36], [57, 20]],
    [[[34, 54], [66, 54], [67, 61], [33, 61]]]],
  ['cauldron', 'Cauldron', oval(50, 60, 40, 30, 0, 36), [], [[box(6, 24, 94, 36), []], [[[22, 82], [32, 86], [18, 97]], []], [[[78, 82], [68, 86], [82, 97]], []]]],
  ['potion', 'Potion', mirror([[50, 4], [58, 4], [58, 10], [56, 12], [56, 34], [78, 52], [84, 70], [78, 86], [64, 94], [50, 96]]),
    [oval(42, 70, 5, 5, 0, 12), oval(60, 62, 3.5, 3.5, 0, 10)]],
  ['candle', 'Candle', [[50, 4], [60, 22], [58, 36], [53, 42], [53, 48], [62, 48], [62, 94], [38, 94], [38, 48], [47, 48], [47, 42], [42, 36], [40, 22]]],
  ['flame', 'Flame', [[50, 4], [62, 24], [74, 38], [80, 58], [74, 80], [60, 94], [40, 94], [26, 80], [20, 60], [28, 40], [36, 48], [40, 30]]],
  ['tombstone', 'Tombstone', mirror([[50, 6], [66, 8], [78, 16], [84, 30], [84, 94], [50, 94]]),
    [[[46, 28], [54, 28], [54, 38], [64, 38], [64, 46], [54, 46], [54, 70], [46, 70], [46, 46], [36, 46], [36, 38], [46, 38]]]],
  ['cross', 'Cross', [[42, 4], [58, 4], [58, 26], [80, 26], [80, 40], [58, 40], [58, 96], [42, 96], [42, 40], [20, 40], [20, 26], [42, 26]]],
  ['coffin', 'Coffin', [[34, 4], [66, 4], [82, 28], [70, 96], [30, 96], [18, 28]]],
  ['house', 'House', [[10, 96], [10, 50], [4, 50], [30, 24], [30, 8], [38, 8], [38, 16], [50, 6], [96, 50], [90, 50], [90, 96]],
    [box(20, 58, 34, 72), box(58, 64, 72, 90), oval(50, 36, 6, 6, 0, 12)]],
  ['tree', 'Bare tree', [[44, 96], [46, 70], [30, 52], [14, 46], [10, 36], [18, 42], [32, 46], [22, 26], [26, 14], [30, 28], [40, 46], [46, 40], [44, 20],
    [50, 6], [53, 22], [54, 40], [62, 34], [72, 16], [80, 10], [76, 22], [66, 40], [60, 52], [78, 46], [92, 34], [88, 48], [70, 58], [56, 68], [58, 96]]],
  ['hand', 'Hand', [[30, 96], [28, 60], [16, 46], [12, 38], [18, 36], [30, 48], [30, 16], [36, 12], [40, 16], [42, 40], [44, 8], [50, 4], [54, 8], [54, 40],
    [58, 12], [64, 10], [67, 14], [64, 42], [70, 22], [76, 20], [78, 26], [74, 60], [66, 96]]],
  ['claws', 'Claw marks', [[18, 12], [26, 8], [40, 88], [34, 92]], [], [[[[42, 6], [50, 4], [62, 92], [56, 95]], []], [[[66, 12], [74, 10], [82, 86], [76, 88]], []]]],
  ['fangs', 'Fangs', [[6, 20], [94, 20], [94, 32], [74, 32], [66, 80], [58, 32], [42, 32], [34, 80], [26, 32], [6, 32]]],
  ['grin', 'Grin', [[6, 36], [20, 46], [28, 40], [36, 52], [44, 44], [50, 56], [56, 44], [64, 52], [72, 40], [80, 46], [94, 36], [86, 62], [70, 76], [50, 82], [30, 76], [14, 62]]],
  ['eyes', 'Eyes', oval(28, 50, 21, 14, 0, 24), [oval(30, 50, 6, 8, 0, 12)], [[oval(72, 50, 21, 14, 0, 24), [oval(70, 50, 6, 8, 0, 12)]]]],
  ['eye', 'Eye', almond(), [oval(50, 50, 14, 14)]],
  ['keyhole', 'Keyhole', oval(50, 34, 19, 19), [], [[[[42, 44], [58, 44], [66, 92], [34, 92]], []]]],
  ['mushroom', 'Toadstool', arc(50, 54, 46, 46, PI, 2 * PI, 24), [oval(32, 36, 6, 5, 0, 12), oval(58, 24, 7, 6, 0, 12), oval(74, 44, 5, 4, 0, 10)],
    [[[[40, 50], [60, 50], [64, 95], [36, 95]], []]]],
  ['leaf', 'Leaf', mirror(Array.from({ length: 13 }, (_, i) => { const u = i / 12; return [50 + 30 * Math.sin(PI * u) ** 0.9, 6 + 80 * u] as Pt; })),
    [box(49, 20, 51, 76)], [[[[48, 84], [52, 84], [54, 98], [50, 98]], []]]],
  ['broom', 'Broom', [[86, 4], [93, 9], [52, 56], [45, 51]], [], [[[[42, 46], [58, 62], [32, 94], [18, 92], [6, 84], [8, 70]], []]]],
  ['candy', 'Sweet', oval(50, 50, 22, 18, 0, 24), [], [[[[30, 50], [6, 32], [11, 50], [6, 68]], []], [[[70, 50], [94, 32], [89, 50], [94, 68]], []]]],
  ['lollipop', 'Lollipop', oval(50, 36, 32, 32, 0, 32), [], [[box(47, 60, 53, 96), []]]],
  ['candy-corn', 'Candy corn', candyCorn(), [[[33, 54], [67, 54], [70, 62], [30, 62]], [[24, 76], [76, 76], [77, 82], [23, 82]]]],
  ['paw', 'Paw', oval(50, 66, 24, 20, 0, 28), [], [[oval(22, 40, 9, 11, -0.3), []], [oval(40, 25, 9, 11, -0.1), []], [oval(60, 25, 9, 11, 0.1), []], [oval(78, 40, 9, 11, 0.3), []]]],
];

export const SYMBOLS: Symbol[] = list.map(([key, label, outer, holes = [], more = []]) => ({ key, label, outer, holes, more: more.map(([o, h]) => ({ outer: o, holes: h })) }));

/** the symbol as one ring: the outline, then each hole wound the other way and reached by a seam from the first point */
export function symbolRing(s: Symbol): Pt[] {
  const up = (p: Pt[]) => (area(p) < 0 ? p.slice().reverse() : p), down = (p: Pt[]) => (area(p) > 0 ? p.slice().reverse() : p);
  const o = up(s.outer), ring: Pt[] = [...o];
  const visit = (p: Pt[]) => ring.push(o[0], p[0], ...p.slice(1), p[0], o[0]);
  for (const m of s.more) visit(up(m.outer));
  for (const h of [...s.holes, ...s.more.flatMap(m => m.holes)]) visit(down(h));
  return ring;
}

/** the symbol's path for an inline SVG */
export const symbolPath = (s: Symbol) => 'M' + symbolRing(s).map(p => p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join('L') + 'Z';

/** points on the sheet (y up), the symbol `size` across and centred on `at` */
export function symbolOnSheet(s: Symbol, size: number, at: Pt): Pt[] {
  return symbolRing(s).map(([x, y]) => [+(at[0] + ((x - 50) / 100) * size).toFixed(4), +(at[1] - ((y - 50) / 100) * size).toFixed(4)] as Pt);
}
